import type { JSONContent } from "@tiptap/react";

export interface ReportDocumentUpdate {
  structuredEditorState: JSONContent;
  reportExamType?: string;
}
export interface RevisionedReportUpdate extends ReportDocumentUpdate {
  expectedRevision: string;
  operationId: string;
}

/** One editor's save sequence. Keeps only the current request and latest edit.
 * Unknown outcomes retry the identical request before publishing newer edits.
 * A conflict never adopts a remote revision or mutates the caller's document.
 */
export function createReportSaveQueue(
  initialRevision: string,
  persist: (update: RevisionedReportUpdate) => Promise<{ report_revision?: string | null }>,
) {
  let revision = initialRevision;
  let desired: ReportDocumentUpdate | undefined;
  let pending: RevisionedReportUpdate | undefined;
  let persisted: string | undefined;
  let flight: Promise<string> | undefined;
  let conflict: unknown;

  async function drain(): Promise<string> {
    while (pending || (desired && JSON.stringify(desired) !== persisted)) {
      pending ??= { ...desired!, expectedRevision: revision, operationId: crypto.randomUUID() };
      try {
        const result = await persist(pending);
        if (!result.report_revision) throw new Error("Save did not return a report revision.");
        revision = result.report_revision;
        const { expectedRevision: _revision, operationId: _operation, ...document } = pending;
        persisted = JSON.stringify(document);
        pending = undefined;
      } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 409)
          conflict = error;
        throw error;
      }
    }
    return revision;
  }

  function settle(): Promise<string> {
    if (!flight)
      flight = drain().finally(() => {
        flight = undefined;
      });
    return flight.then(() => {
      // A caller can enqueue after drain exits but before its finally runs.
      // Recheck after settlement so that caller cannot receive an older revision.
      if (pending || (desired && JSON.stringify(desired) !== persisted)) return settle();
      return revision;
    });
  }

  return {
    save(update: ReportDocumentUpdate): Promise<string> {
      // Snapshot callers' mutable objects so a retry cannot acquire new wording.
      desired = structuredClone(update);
      if (conflict) return Promise.reject(conflict);
      return settle();
    },
  };
}
